import { createTheme } from '@mui/material';

interface ColorShades {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  A100: string;
  A200: string;
  A400: string;
  A700: string;
}

export interface CustomColorPalette {
  blue: ColorShades;
  neutral: ColorShades;
  gray: ColorShades;
}

interface SemanticColors {
  // Backgrounds
  background: string;
  backgroundSecondary: string;
  surface: string;
  surfaceHover: string;

  // Borders
  border: string;
  borderSecondary: string;
  borderDashed: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;

  // Icons
  icon: string;
  iconSecondary: string;
  iconBackground: string;

  // Buttons
  buttonBackground: string;
  buttonBackgroundHover: string;
  buttonText: string;

  // Interactive elements
  tabActive: string;
  tabInactive: string;
  tabTextActive: string;
  tabTextInactive: string;

  // Menu/Dropdown
  menuBackground: string;
  menuBorder: string;
  menuItemHover: string;

  // Widget specific
  widgetBackground: string;
  widgetBorder: string;
  widgetTitle: string;

  // Dashboard specific
  dashboardBackground: string;
}

declare module '@mui/material/styles' {
  interface Theme {
    customColorPalette: CustomColorPalette;
    semanticColors: SemanticColors;
  }

  interface ThemeOptions {
    customColorPalette: CustomColorPalette;
    semanticColors: SemanticColors;
  }
}

const grayPalette: ColorShades = {
  50: '#F9FAFB',
  100: '#F2F4F7',
  200: '#EAECF0',
  300: '#D0D5DD',
  400: '#98A2B3',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#1D2939',
  900: '#101828',
  A100: '#FFFFFF',
  A200: '#F2F4F7',
  A400: '#D0D5DD',
  A700: '#98A2B3',
};

export const createAppTheme = (mode: 'light' | 'dark' = 'light') => {
  const palette = {
    blue: {
      50: '#E7F0FF',
      100: '#C4CFFF',
      200: '#9AAAFF',
      300: '#7388F7',
      400: '#546BEA',
      500: '#3650DE',
      600: '#3248D4',
      700: '#2B3DC6',
      800: '#2133BA',
      900: '#141BA1',
      A100: '#869FFF',
      A200: '#5169FF',
      A400: '#3755FF',
      A700: '#3946FF',
    },
    neutral: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#f5f5f5',
      A200: '#eeeeee',
      A400: '#bdbdbd',
      A700: '#616161',
    },
    gray: grayPalette,
  };

  // Define semantic colors based on mode
  const semanticColors: SemanticColors = mode === 'dark'
    ? {
      // Dark mode semantic colors
      background: palette.gray[900],
      backgroundSecondary: palette.gray[900],
      surface: palette.gray[800],
      surfaceHover: palette.gray[700],

      border: palette.gray[700],
      borderSecondary: palette.gray[700],
      borderDashed: palette.gray[700],

      textPrimary: palette.gray[200],
      textSecondary: palette.gray[300],
      textTertiary: palette.gray[400],
      textMuted: palette.gray[500],

      icon: palette.gray[300],
      iconSecondary: palette.gray[400],
      iconBackground: palette.gray[700],

      buttonBackground: palette.gray[800],
      buttonBackgroundHover: palette.gray[700],
      buttonText: palette.gray[200],

      tabActive: palette.blue[500],
      tabInactive: palette.gray[800],
      tabTextActive: '#FFFFFF',
      tabTextInactive: palette.gray[200],

      menuBackground: palette.gray[800],
      menuBorder: palette.gray[700],
      menuItemHover: 'rgba(255, 68, 68, 0.2)',

      widgetBackground: palette.gray[900],
      widgetBorder: palette.gray[700],
      widgetTitle: palette.gray[200],

      dashboardBackground: palette.gray[800],
    }
    : {
      // Light mode semantic colors
      background: '#FFFFFF',
      backgroundSecondary: 'transparent',
      surface: '#FFFFFF',
      surfaceHover: palette.gray[100],

      border: '#DDDDDD',
      borderSecondary: palette.gray[300],
      borderDashed: 'rgba(208, 213, 221, 1)',

      textPrimary: '#333333',
      textSecondary: palette.gray[600],
      textTertiary: palette.gray[400],
      textMuted: palette.gray[500],

      icon: '#667085',
      iconSecondary: palette.gray[500],
      iconBackground: '#3650DE',

      buttonBackground: '#FFFFFF',
      buttonBackgroundHover: 'rgba(255, 255, 255, 0.9)',
      buttonText: palette.gray[900],

      tabActive: palette.blue[500],
      tabInactive: 'rgba(242, 244, 247, 1)',
      tabTextActive: '#FFFFFF',
      tabTextInactive: 'rgba(16, 24, 40, 1)',

      menuBackground: palette.gray[100],
      menuBorder: "#DDDDDD",
      menuItemHover: 'rgba(255, 68, 68, 0.1)',

      widgetBackground: '#FFFFFF',
      widgetBorder: '#DDDDDD',
      widgetTitle: '#333333',

      dashboardBackground: '#f8f9fa',
    };

  return createTheme({
    palette: {
      mode,
      primary: {
        main: palette.blue[500],
      },
    },
    customColorPalette: palette,
    semanticColors: semanticColors,
    components: {
      MuiSlider: {
        styleOverrides: {
          rail: {
            color: palette.gray[300],
            opacity: 1,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            fontSize: '12px',
            lineHeight: '16px',
            color: palette.gray[400],
            fontWeight: 500,
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          colorPrimary: {
            '&.Mui-checked': {
              color: palette.gray.A100,
            },
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: '16px',
          },
        },
        variants: [
          {
            props: { variant: 'outlined' },
            style: {
              padding: '10px 22px',
              boxShadow: 'unset',
            },
          },
          {
            props: { variant: 'contained' },
            style: {
              padding: '10px 22px',
            },
          },
        ],
      },

      MuiTextField: {
        defaultProps: {
          size: 'small',
          sx: {
            '.MuiInputLabel-root': {
              //label fontsize
              fontSize: '16px !important',
            },
          },
        },

        styleOverrides: {
          root: {
            backgroundColor: palette.gray.A100,
          },
        },
      },

      MuiSelect: {
        defaultProps: {
          size: 'small',
          sx: {
            bgcolor: palette.gray[200],
            borderRadius: '8px',
            border: 'none',
            height: '30px',
            textTransform: 'capitalize',
            mb: '16px',
            boxShadow: 'none',
            '.MuiOutlinedInput-notchedOutline': { border: 0 },
          },
        },
        styleOverrides: {
          root: {
            backgroundColor: palette.gray.A100,
          },
        },
      },
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      allVariants: {
        lineHeight: '17px',
        fontFamily: 'Roboto, sans-serif',
      },
      h1: {
        fontSize: '24px',
        fontWeight: 600,
        lineHeight: '29px',
      },
      h3: {
        fontSize: '20px',
        fontWeight: 600,
        lineHeight: '29px',
      },
      h6: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: '16px',
        fontWeight: 500,
      },
      body1: {
        fontSize: '16px',
      },
      body2: {
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '0.17px',
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 500,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    }
  });
};

export const theme = createAppTheme('light');

